import { useEffect, useRef, useMemo } from "react";
import {
  getBoardColumns,
  getDropdownColumns,
} from "../services/mondayApiService";
import { BOARDS_TO_CHECK, KNOWN_PERSON_COLUMN_IDS } from "../constants/columnIds";
import { useMondayContext } from "./useMondayContext";

/**
 * usePersonColumn - Hook to find and manage person column data
 * Implements caching: checks context first, only fetches if data doesn't exist
 */
export function usePersonColumn() {
  const {
    personColumnId,
    personColumnIdMap,
    availablePeople,
    updatePersonColumnId,
    updatePersonColumnIdMap,
    updateAvailablePeople,
  } = useMondayContext();

  const isFetchingPersonColumn = useRef(false);
  const hasFetchedPersonColumn = useRef(false);

  // Check if we have person columns to avoid object reference issues
  const personColumnMapKeysCount = Object.keys(personColumnIdMap).length;
  const hasPersonColumnMap = personColumnMapKeysCount > 0;

  // Find Person column - check both TASKS and Weekly Checks boards (only once per session)
  useEffect(() => {
    // Check cache first - if data exists, return early
    if (hasPersonColumnMap || hasFetchedPersonColumn.current) {
      hasFetchedPersonColumn.current = true;
      return; // Data already cached, no need to fetch
    }

    // Prevent duplicate fetches
    if (isFetchingPersonColumn.current) {
      return;
    }

    const findPersonColumn = async () => {
      isFetchingPersonColumn.current = true;
      try {
        // Get the current map (should already be initialized by the previous useEffect)
        const personColumnMap = { ...personColumnIdMap };
        
        // If map is still empty, initialize with known IDs
        if (Object.keys(personColumnMap).length === 0) {
          const knownPersonColumnIds = KNOWN_PERSON_COLUMN_IDS;
          Object.keys(knownPersonColumnIds).forEach(boardIdStr => {
            const boardId = Number(boardIdStr);
            personColumnMap[boardId] = knownPersonColumnIds[boardId];
          });
          updatePersonColumnIdMap(personColumnMap);
        }
        
        hasFetchedPersonColumn.current = true;
        
        // Set the first person column ID found for backward compatibility
        const firstPersonColId = Object.values(personColumnMap)[0];
        if (firstPersonColId) {
          updatePersonColumnId(firstPersonColId);
          
          // Fetch dropdown options from all boards that have person columns (batched)
          // Only if we don't already have people cached
          const hasPeople = availablePeople.length > 0;
          if (!hasPeople) {
            try {
              const allDropdownOptions = new Set();
              
              // Prepare board-column pairs for batched query
              const boardColumnPairs = Object.entries(personColumnMap).map(([boardIdStr, columnId]) => ({
                boardId: boardIdStr,
                columnId: columnId,
              }));
              
              // Fetch all dropdown columns in a single batched query
              const dropdownColumns = await getDropdownColumns({ boardColumnPairs });
              
              // Process each dropdown column result
              dropdownColumns.forEach((dropdownColumn, index) => {
                if (dropdownColumn && dropdownColumn.settings_str) {
                  try {
                    const settings = JSON.parse(dropdownColumn.settings_str);
                    // Dropdown settings can contain labels array or labels object
                    if (settings.labels) {
                      if (Array.isArray(settings.labels)) {
                        settings.labels.forEach(label => {
                          const labelText = typeof label === 'string' ? label : (label.label || label.name || String(label));
                          if (labelText) allDropdownOptions.add(labelText);
                        });
                      } else if (typeof settings.labels === 'object') {
                        // Labels might be an object with IDs as keys
                        Object.values(settings.labels).forEach(label => {
                          const labelText = typeof label === 'string' ? label : (label.label || label.name || String(label));
                          if (labelText) allDropdownOptions.add(labelText);
                        });
                      }
                    }
                  } catch (e) {
                    const boardIdStr = boardColumnPairs[index]?.boardId;
                    console.error(`Error parsing dropdown settings for board ${boardIdStr}:`, e);
                  }
                }
              });
              
              // If we got dropdown options, store them in cache
              if (allDropdownOptions.size > 0) {
                const people = Array.from(allDropdownOptions)
                  .map((name) => ({ id: name, name }))
                  .sort((a, b) => a.name.localeCompare(b.name));
                
                updateAvailablePeople(people);
              }
            } catch (err) {
              console.error("Error fetching dropdown options:", err);
              // Continue without dropdown options - will extract from tasks instead
            }
          }
        }
      } catch (err) {
        console.error("Error finding person column:", err);
        // Allow retry on error
      } finally {
        isFetchingPersonColumn.current = false;
      }
    };

    findPersonColumn();
  }, [personColumnMapKeysCount, availablePeople.length, updatePersonColumnId, updatePersonColumnIdMap, updateAvailablePeople]);

  return {
    personColumnId,
    personColumnIdMap,
    availablePeople,
    setAvailablePeople: updateAvailablePeople, // Expose update function for compatibility
  };
}
